import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { JhiEventManager, JhiLanguageService } from '../../shared/core-utils';
import { AlertService } from '../../shared/alert/alert.service';
import { MultiTraitQueryService } from '../multi-trait-query.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MultiTraitQueryContext } from '../multi-trait-query-context';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { finalize } from 'rxjs/internal/operators/finalize';
import { Subscription } from 'rxjs';
import { TraitFilterContext } from '../setup-trait-filter-step-panel/trait-filter-context';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { ColumnCategory, ColumnModel } from '../../shared/query/model/column.model';
import { Weight } from '../../shared/query/model/weight.model';
import { TrialEnvironmentSearchResponse } from '../../shared/query/model/trial-environment-search-response.model';

@Component({
    selector: 'jhi-step-specify-and-weight-the-environments',
    styleUrls: ['./step-specify-and-weight-the-environments.component.css'],
    templateUrl: './step-specify-and-weight-the-environments.component.html'
})
export class StepSpecifyAndWeightTheEnvironmentsComponent implements OnInit {

    static readonly SORT_ENV_NO = 'gp.nd_geolocation_id';
    subscriptionManager = new SubscriptionsManager();
    @Output() next = new EventEmitter<void>();
    @Output() back = new EventEmitter<void>();
    @Input() active = false;
    environmentWeightOptions = [
        { label: 'head-to-head-query.environment.weight.important', value: Weight.IMPORTANT },
        { label: 'head-to-head-query.environment.weight.critical', value: Weight.CRITICAL },
        { label: 'head-to-head-query.environment.weight.desirable', value: Weight.DESIRABLE },
        { label: 'head-to-head-query.environment.weight.ignored', value: Weight.IGNORED }
    ];
    ColumnLabels = ColumnLabels;
    traitEnvironments: TrialEnvironmentSearchResponse[];
    environmentDetailColumns: ColumnModel[];
    environmentHiddenColumns = {};

    isLoading: boolean;
    selectedItems: Map<number, TrialEnvironmentSearchResponse> = new Map<number, TrialEnvironmentSearchResponse>();
    lastClickIndex: any;
    totalItems: number;
    itemsPerPage: number = 20;
    page: number;
    previousPage: number;
    predicate: any;
    reverse: boolean;
    isSelectAll: boolean;
    eventSubscriber: Subscription;
    selectedTraits: number[];
    protected readonly Weight = Weight;
    protected readonly staticColumns = [
        new ColumnModel(-1, 'multi-trait-query.panel.specify.and.weight.the.environments.environment.table.header.environment.no', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-2, 'multi-trait-query.panel.specify.and.weight.the.environments.environment.table.header.location', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-3, 'multi-trait-query.panel.specify.and.weight.the.environments.environment.table.header.country', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-4, 'multi-trait-query.panel.specify.and.weight.the.environments.environment.table.header.study', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-5, 'multi-trait-query.panel.specify.and.weight.the.environments.environment.table.header.weight', ColumnCategory.STATIC, true, null, true)
    ];

    constructor(private activatedRoute: ActivatedRoute,
                private eventManager: JhiEventManager,
                private jhiLanguageService: JhiLanguageService,
                private alertService: AlertService,
                private traitFilterContext: TraitFilterContext,
                public queryContext: MultiTraitQueryContext,
                private multiTraitQueryService: MultiTraitQueryService,
                private router: Router) {
        this.selectedTraits = [];
    }

    get hiddenColumns() {
        return this.environmentHiddenColumns;
    }

    set hiddenColumns(hiddenColumns) {
        this.environmentHiddenColumns = hiddenColumns;
    }

    ngOnInit(): void {
        this.page = 1;
        this.previousPage = 1;
        this.totalItems = 0;
        this.isSelectAll = false;
        this.clearSort();
        this.traitEnvironments = [];
        this.environmentDetailColumns = [];
        this.registerEnvironmentFilterChange();
    }

    registerEnvironmentFilterChange() {
        this.eventSubscriber = this.eventManager.subscribe('environmentFilterViewChanged', (event) => {
            if (event.content.locationFilter) {
                this.queryContext.environmentSearch.locationFilter = event.content.locationFilter;
            }
            if (event.content.studyFilter) {
                this.queryContext.environmentSearch.studyFilter = event.content.studyFilter;
            }
            this.resetTable();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['active'] && this.active) {
            this.traitEnvironments = [];
            this.onColumnsSelected(this.queryContext.getEnvironmentColumns());
            this.selectedItems = this.queryContext.getEnvironmentSelectedItems();
            this.isSelectAll = this.queryContext.getSelectedAllEnvironments();
            this.page = 1;
            this.previousPage = 1;
        }
    }

    openLocationFilterModal() {
        this.router.navigate(['/', { outlets: { popup: 'environments-location-filter-dialog' }, }], {
            replaceUrl: true,
            queryParamsHandling: 'merge',
            queryParams: {
                selectedTraits: this.queryContext.getSelectedTraits(),
                locationFilter: this.queryContext.environmentSearch.locationFilter
            }
        });
    }

    openStudyFilterModal() {
        this.router.navigate(['/', { outlets: { popup: 'environments-study-filter-dialog' }, }], {
            replaceUrl: true,
            queryParamsHandling: 'merge',
            queryParams: {
                selectedTraits: this.queryContext.getSelectedTraits(),
                studyFilter: this.queryContext.environmentSearch.studyFilter
            }
        });
    }

    isPageSelected() {
        return this.size() && this.traitEnvironments.every((entry: TrialEnvironmentSearchResponse) => Boolean(this.selectedItems.get(entry.id)));
    }

    size() {
        return this.isSelectAll ? this.totalItems : this.selectedItems.size;
    }

    onSelectPage() {
        if (this.isPageSelected()) {
            // remove all items
            this.traitEnvironments.forEach((entry) => this.selectedItems.delete(entry.id));
        } else {
            // check remaining items
            this.traitEnvironments.forEach((entry) => {
                entry.weight = Weight.IMPORTANT;
                this.selectedItems.set(entry.id, entry);
            });
        }
    }

    onSelectAll(isSelectAll) {
        this.isSelectAll = !isSelectAll;
        this.queryContext.setSelectedAllEnvironments(this.isSelectAll);
        this.clearSelectedItems();
        if (this.isSelectAll) {
            this.isLoading = true;
            const request = this.queryContext.environmentSearch;
            request.envConditions = [];
            this.subscriptionManager.add = this.multiTraitQueryService.getSearchResults(request,
                {
                    page: this.page - 1,
                    size: this.totalItems,
                    sort: this.getSort()
                }).pipe(finalize(() => {
                this.isLoading = false;
            })).subscribe(
                (res: HttpResponse<TrialEnvironmentSearchResponse[]>) => {
                    res.body.forEach((item: TrialEnvironmentSearchResponse) => {
                        item.weight = Weight.IMPORTANT;
                        this.selectedItems.set(item.id, item);
                    });
                });
        }
    }

    clearSelectedItems() {
        this.selectedItems.clear();
    }

    onClearSort($event) {
        $event.preventDefault();
        this.clearSort();
        this.transition();
    }

    toggleSelect($event, index, entry: TrialEnvironmentSearchResponse, checkbox = false) {
        if (this.isSelectAll) {
            return;
        }
        if (!$event.ctrlKey && !checkbox) {
            this.clearSelectedItems();
        }
        let items;
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1,
                min = Math.min(this.lastClickIndex, index);
            items = this.traitEnvironments.slice(min, max);
        } else {
            items = [entry];
            this.lastClickIndex = index;
        }
        const isClickedItemSelected = this.selectedItems.has(entry.id);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems.delete(item.id);
            } else {
                item.weight = Weight.IMPORTANT;
                this.selectedItems.set(item.id, item);
            }
        }
    }

    isSelected(entry: TrialEnvironmentSearchResponse) {
        return this.selectedItems.get(entry.id);
    }

    loadAll() {
        this.selectedTraits = this.queryContext.getSelectedTraits();
        this.isLoading = true;
        this.subscriptionManager.add = this.multiTraitQueryService.getSearchResults(this.queryContext.environmentSearch,
            {
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.getSort()
            }
        ).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<TrialEnvironmentSearchResponse[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.transition();
        }
    }

    transition() {
        this.router.navigate(['./'], {
            queryParamsHandling: 'merge',
            queryParams: {
                page: this.page,
                size: this.itemsPerPage,
                sort: this.getSort()
            }, relativeTo: this.activatedRoute
        });
        this.loadAll();
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.isSelectAll = false;
        this.clearSelectedItems();
        this.loadAll();
    }

    getSort() {
        if (!this.predicate) {
            return '';
        }
        return [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
    }

    isNextEnabled(): boolean {
        return this.selectedItems.size > 0;
    }

    trackId(index: number, item: any) {
        return item.id;
    }

    onColumnsSelected(columns: ColumnModel[]) {
        this.hiddenColumns = this.getHiddenColumns(columns);
        this.queryContext.cleanEnvColumns();
        this.queryContext.setEnvironmentColumns(columns);
        this.environmentDetailColumns = [];
        columns.forEach(column => {
            if (column.id > 0 && column.selected) {
                this.queryContext.addEnvConditions(column);
                this.environmentDetailColumns.push(column);
            }
        });
        this.traitEnvironments = [];
        this.clearSort();
        this.loadAll();
    }

    onNext(): void {
        if (this.isNextEnabled()) {
            this.queryContext.setEnvironmentSelectedItems(this.selectedItems);
            this.traitFilterContext.initialize();
            this.next.emit();
        }
    }

    onBack(): void {
        this.back.emit();
    }

    selectedWeight(traitEnvironment: TrialEnvironmentSearchResponse) {
        const traitEnv = this.isSelected(traitEnvironment);
        if (traitEnv) {
            traitEnv.weight = traitEnvironment.weight;
        }
    }

    hasFilterApplied() {
        return this.queryContext.environmentSearch != null && (this.queryContext.environmentSearch.locationFilter.length > 0 || this.queryContext.environmentSearch.studyFilter.length > 0);
    }

    colspan() {
        const countEnvCon = this.environmentDetailColumns.length;
        return this.isSelectAll ? countEnvCon + 5 : countEnvCon + 6;
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
        this.eventSubscriber.unsubscribe();
    }

    private clearSort() {
        this.predicate = StepSpecifyAndWeightTheEnvironmentsComponent.SORT_ENV_NO;
        this.reverse = true;
    }

    private onSuccess(data: TrialEnvironmentSearchResponse[], headers) {
        this.totalItems = parseInt(headers.get('X-Total-Count'), 10);
        this.traitEnvironments = data;
        this.traitEnvironments.forEach(traitEnvironment => {
            traitEnvironment.weight = this.selectedItems.has(traitEnvironment.id) ?
                this.selectedItems.get(traitEnvironment.id).weight : Weight.IGNORED;
        });
    }

    private getHiddenColumns(selectedColumns): ColumnModel[] {
        const unselectedColumns: ColumnModel[] = selectedColumns.filter((column: ColumnModel) => !column.selected);
        const newHiddenColumns = [];
        unselectedColumns.forEach((column) => {
            newHiddenColumns[ColumnLabels[column.name]] = true;
        });
        return newHiddenColumns;
    }
}

export enum ColumnLabels {
    'ENV' = 'ENV',
    'LOCATION' = 'LOCATION',
    'COUNTRY' = 'COUNTRY',
    'STUDY' = 'STUDY',
    'WEIGHT' = 'WEIGHT'
}
